/**
 * Navegacao, derivada dos manifestos.
 *
 * A casca nao conhece nenhum dominio (regra 1): nao ha aqui "financas" nem
 * "saude". Ha "as rotas principais que os plugins declararam e que esta pessoa
 * pode abrir". Acrescentar um plugin acrescenta um destino sem tocar em nada
 * deste arquivo.
 *
 * Restricao de ADR-0007: no maximo 5 destinos, cada um com **icone E palavra**,
 * e toda a acao principal a dois toques do inicio.
 */
import type { PluginManifest } from '@flos/shared';
import type { RegisteredRoute } from '@flos/core';

/** Caminhos da propria casca. Em portugues, como tudo o que se ve. */
export const SHELL_PATHS = {
  HOME: '/',
  ASSISTANT: '/assistente',
  AREAS: '/areas',
  SETTINGS: '/configuracoes',
  CONFLICTS: '/conflitos',
  ONBOARDING: '/entrar',
} as const;

export interface NavItem {
  to: string;
  icon: string;
  label: string;
  /** O assistente e o destino visualmente dominante: a voz e a entrada principal. */
  emphasis?: boolean;
  /** Numero a mostrar no canto (conflitos por rever, por exemplo). */
  badge?: number;
}

/** Quantos destinos de plugin cabem na barra sem partir a regra dos 5. */
const PLUGIN_SLOTS = 2;

export interface BuildNavInput {
  routes: readonly RegisteredRoute[];
  manifests: readonly PluginManifest[];
  conflicts?: number;
}

export function primaryRoutes(routes: readonly RegisteredRoute[]): RegisteredRoute[] {
  return routes.filter((route) => route.primary === true);
}

function iconFor(manifests: readonly PluginManifest[], pluginId: string): string {
  return manifests.find((manifest) => manifest.id === pluginId)?.icon ?? '📁';
}

/**
 * A barra de baixo. Sempre com Inicio, sempre com Assistente, sempre com
 * Configuracoes — sao os tres lugares de onde se sai de qualquer engano.
 */
export function buildNavItems(input: BuildNavInput): NavItem[] {
  const plugins = primaryRoutes(input.routes)
    .slice(0, PLUGIN_SLOTS)
    .map((route) => ({
      to: route.fullPath,
      icon: iconFor(input.manifests, route.pluginId),
      label: route.title,
    }));

  const items: NavItem[] = [
    { to: SHELL_PATHS.HOME, icon: '🏠', label: 'Início' },
    ...plugins,
    { to: SHELL_PATHS.ASSISTANT, icon: '🎤', label: 'Assistente', emphasis: true },
    {
      to: SHELL_PATHS.SETTINGS,
      icon: '⚙️',
      label: 'Configurações',
      ...(input.conflicts ? { badge: input.conflicts } : {}),
    },
  ];

  return items.slice(0, 5);
}

/**
 * Decide se um caminho esta ativo. `/` so casa exatamente, senao o Inicio
 * ficava marcado em todos as telas.
 */
export function isActivePath(current: string, target: string): boolean {
  if (target === SHELL_PATHS.HOME) return current === '/' || current === '';
  return current === target || current.startsWith(`${target}/`);
}
